import Paper from '@base/Paper/Paper';
import useQuestion from '@cont/Quiz/Taking/Objective/Question/useQuestion';
import styled from '@emotion/styled';
import { Spacings } from '@styles/index';
import { useEffect, useState } from 'react';
import HTMLWrapper from '@comp/Question/HTMLWrapper';
import { setCurrentSelectedAnswer } from '@actions/quizTaking';
import { useDispatch } from 'react-redux';
import Button from '@base/Button/Button';
import useMaxAttemptQuestions from '@cont/Quiz/Taking/Common/useMaxAttemptQuestions';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import { getCurrentQuizType } from '@selectors/quizTaking';
import Dangerous from '@educator/Container/AutoDPP/DangerousContent';
import { mediaQuery } from '@cont/Quiz/constants';
import Options from '../Options';
import QuestionHeader from '../../Common/QuestionHeader';
import useQuestionStatus from '../../Navigation/useQuestionStatus';

const Wrapper = styled.div`
  margin-bottom: ${Spacings.SPACING_4B};
  user-select: none;
`;

const QuestionWrapper = styled(Paper)`
  padding: ${Spacings.SPACING_6B};
  max-height: ${({ $MaxHeight }) => $MaxHeight || 'calc(100vh - 178px)'};
  overflow: scroll;
  overflow-x: hidden;

  scrollbar-width: none;

  ::-webkit-scrollbar {
    width: 0;
    background: transparent;
  }

  ::-webkit-scrollbar-thumb {
    background: transparent;
  }

  ${mediaQuery.aboveTablet} {
    padding: ${Spacings.SPACING_8B};
  }

  ${mediaQuery.aboveLargeScreen} {
    padding: ${Spacings.SPACING_10B};
  }
`;

const StyledButton = styled(Button)`
  width: 200px;
  padding: 0;
  min-height: ${Spacings.SPACING_12B};
  height: ${Spacings.SPACING_12B};
`;

const Question = ({ maxHeight }) => {
  const {
    sectionUID,
    questionUID,
    currentQuestion,
    currentLanguage,
    contentLanguages,
    handleLanguageChange,
    handleOnQuestionView
  } = useQuestion();

  const { isAttempted } = useQuestionStatus(sectionUID, questionUID);
  const { isQuestionAttemptable } = useMaxAttemptQuestions(sectionUID);

  const dispatch = useDispatch();

  const [isQuestionMarkedAsAttempted, setIsQuestionMarkedAsAttempted] =
    useState(isAttempted);

  const [isAttemptBtnClicked, setIsAttemptBtnClicked] = useState(false);

  const { isSubjective } = useSelectorWithShallowEqual(getCurrentQuizType);

  useEffect(() => {
    if (isSubjective && isAttemptBtnClicked) {
      dispatch(
        setCurrentSelectedAnswer([isQuestionMarkedAsAttempted ? '1' : '0'])
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isQuestionMarkedAsAttempted, isSubjective]);

  useEffect(() => {
    if (isSubjective) {
      setIsQuestionMarkedAsAttempted(isAttempted);
    }
    handleOnQuestionView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionUID]);

  const handleOnAttemped = () => {
    setIsAttemptBtnClicked(true);
    setIsQuestionMarkedAsAttempted((markStatus) => !markStatus);
  };

  const getAttemptBtnVariant = () => {
    if (!isAttempted && !isQuestionAttemptable) {
      return 'disabled';
    }
    return 'hollow';
  };

  return (
    <QuestionWrapper $MaxHeight={maxHeight}>
      <Wrapper data-testid="objective-question-wrapper">
        <QuestionHeader
          data-testid="question-header"
          sectionUID={sectionUID}
          questionUID={questionUID}
          currentLanguage={currentLanguage}
          availableLanguages={contentLanguages}
          onLanguageChange={handleLanguageChange}
        />

        <Dangerous
          as={HTMLWrapper}
          data-image-preview
          $isImageClickable={false}
          html={currentQuestion?.content?.[currentLanguage?.code]}
        />
      </Wrapper>
      <Options data-testid="question-options" code={currentLanguage.code} />
      {isSubjective && (
        <StyledButton
          width="200px"
          variant={getAttemptBtnVariant()}
          color="black"
          label={
            isQuestionMarkedAsAttempted
              ? 'Unmark as attempted'
              : 'Mark as attempted'
          }
          onClick={handleOnAttemped}
        />
      )}
    </QuestionWrapper>
  );
};

export default Question;
